import { SearchAIIcon, SearchClearIcon } from 'assets/icons';
import { Loader } from 'components/Loader';
import { appSettings } from 'config/app.settings';
import { useAISearchContext } from 'context/ai-search/ai-search.context';
import { useClickOutsideAndKeyNavigation } from 'hooks/use-click-outside-and-key-navigation.hook';
import React, { FormEvent, KeyboardEvent, MouseEvent, RefObject, useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { ContentUtil } from 'utils/content';

import { AIFeedback } from './AIFeedback';
import { content } from './ai-search.content';
import * as S from './ai-search.styles';

const contentUtil = new ContentUtil({ content, componentName: 'AISearch' });

interface IAISearchProps {
  toggleSearchBar: () => void;
}

interface IAISearchResultProps {
  markdown: string;
}

const AISearchResult = ({ markdown }: IAISearchResultProps) => {
  return <Markdown>{markdown}</Markdown>;
};

export const AISearch = ({ toggleSearchBar }: IAISearchProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const searchInput = useRef<HTMLInputElement>(null);
  const [href, setHref] = useState('');
  const { aiSearchResult, aiSearchId, getAISearchResult, isLoading, hasError, resetError } = useAISearchContext();

  const ref = useClickOutsideAndKeyNavigation((_event) => {
    toggleSearchBar();
  }) as RefObject<HTMLDivElement>;

  useEffect(() => {
    searchInput?.current?.focus();
  }, [searchInput]);

  useEffect(() => {
    if (!hasError) {
      resetError();
    }
  }, [hasError]);

  useEffect(() => {
    setHref(`${appSettings.ENV_ZIGGO_URL}/zoeken/?q=${encodeURI(searchQuery)}`);
  }, [appSettings, searchQuery]);

  const onClickShowAllResults = () => {
    if (searchQuery) {
      const description = 'laad meer resultaten clicked in smart search';
      const status = 'button_click';
      const type = 'search_results';

      triggerDdm(description, type, status, null);
    }
  };

  const triggerDdmButton = (interactionType: string, searchId: null | string) => {
    const description = `${interactionType} button clicked in smart search`;
    const status = 'button_click';
    const type = interactionType;

    triggerDdm(description, status, type, searchId);
  };

  const triggerDdmEnter = (searchId: null | string) => {
    const description = 'enter pressed in smart search';
    const status = 'enter_key';
    const type = 'enter';

    triggerDdm(description, status, type, searchId);
  };

  const triggerDdm = (description: string, status: string, type: string, searchId: null | string) => {
    const location = window.location.pathname;

    const payload = {
      data: {
        type: 'user_event',
        interaction: {
          event: 'smart_search',
          type,
          description,
          text: type === 'reset' ? '' : searchQuery,
          status,
          detail: searchId,
        },
        component: {
          technical_name: 'smartSearch',
          meaningful_name: 'smart_search_block',
          location,
          id: undefined,
          type: undefined,
        },
      },
    };

    window._ddm.trigger('fe.ga_event', payload);
  };

  const submit = async (eventSource: string, interactionType: string) => {
    if (!isLoading && !hasError && searchQuery) {
      try {
        setSubmitted(true);
        const trimmedSearchQuery = searchQuery.trim();
        const searchId = await getAISearchResult(trimmedSearchQuery);
        if (eventSource === 'button_click') {
          triggerDdmButton(interactionType, searchId);
        } else {
          triggerDdmEnter(searchId);
        }
      } finally {
        setSubmitted(true);
      }
    }
  };

  const onClickSubmit = (event: MouseEvent) => {
    event.preventDefault();

    submit('button_click', 'submit');
  };

  const onEnterSubmit = (event: FormEvent) => {
    event.preventDefault();

    submit('enter_key', 'submit');
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      submit('enter_key', 'submit');
    }
  };

  const onClickReset = () => {
    setSearchQuery('');
    setSubmitted(false);
    searchInput?.current?.focus();

    triggerDdmButton('reset', null);
  };

  return (
    <S.SearchContainer ref={ref}>
      <S.SearchForm data-testid="ai-search-popup" onSubmit={onEnterSubmit}>
        <label htmlFor="search-input">
          {contentUtil.translateHtml('searchLabel')}
          <S.SearchInput hasResults={submitted && !hasError}>
            <S.Search
              ref={searchInput}
              maxLength={1024}
              data-testid="search-input"
              name="search-input"
              id="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={onKeyDown}
              onSubmit={onEnterSubmit}
            />
            {searchQuery && (
              <S.ButtonIcon
                aria-label={contentUtil.translateToString('resetAriaLabel')}
                type="button"
                onClick={onClickReset}>
                <SearchClearIcon />
              </S.ButtonIcon>
            )}
            <S.SubmitButton
              aria-label={contentUtil.translateToString('submitAriaLabel')}
              type="submit"
              data-testid="submit-search-query"
              active={Boolean(searchQuery.trim() && !isLoading)}
              onClick={onClickSubmit}>
              <SearchAIIcon />
            </S.SubmitButton>
          </S.SearchInput>
        </label>
      </S.SearchForm>

      <S.SearchOutput aria-live="polite" aria-atomic="false">
        {submitted && !hasError && (
          <S.SearchResult>
            {isLoading && <Loader />}
            {!isLoading && !aiSearchId && (
              <p>
                {contentUtil.translateToString('noResult.pre')}
                <a href={href} onClick={onClickShowAllResults}>
                  {contentUtil.translateToString('noResult.link')}
                </a>
                {contentUtil.translateToString('noResult.post')}
              </p>
            )}
            {!isLoading && aiSearchId && (
              <>
                <AISearchResult markdown={aiSearchResult || ''} />
                <AIFeedback searchId={aiSearchId} />
                <S.Disclaimer>
                  {contentUtil.translateToString('disclaimer.pre')}
                  <a href={href} onClick={onClickShowAllResults}>
                    {contentUtil.translateToString('disclaimer.link')}
                  </a>
                  {contentUtil.translateToString('disclaimer.post')}
                </S.Disclaimer>
              </>
            )}
          </S.SearchResult>
        )}
      </S.SearchOutput>
    </S.SearchContainer>
  );
};
